import { useEffect, useRef, useState, useCallback } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { Camera, CameraOff, Flashlight, FlashlightOff } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { cn } from '@/app/components/ui/utils';
import { dataMatrixDetector, type BoundingBox, decodeWithZxingWasm, decodeDataMatrixWithZxingWasm, getPreprocessedVariants } from '@/lib/ml';

export interface MLScannerProps {
  onScan: (result: string) => void;
  onError?: (error: string) => void;
  onClose?: () => void;
  className?: string;
}

type ScannerState =
  | 'initializing'
  | 'ready'
  | 'detecting'
  | 'decoding'
  | 'error'
  | 'permission_denied';

const SUPPORTED_FORMATS = [
  Html5QrcodeSupportedFormats.DATA_MATRIX,
  Html5QrcodeSupportedFormats.QR_CODE,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E,
];

const INFERENCE_INTERVAL_MS = 333;
const FULL_FRAME_DECODE_INTERVAL_MS = 500;
const REQUIRED_STABLE_FRAMES = 2;
const BBOX_SIMILARITY_THRESHOLD = 0.1;

// Native BarcodeDetector API (Chrome 83+ on Android, Safari 16.4+)
// Much better Data Matrix support than zxing-js
interface NativeBarcodeDetectorResult {
  rawValue: string;
  format: string;
  boundingBox: DOMRectReadOnly;
  cornerPoints: Array<{ x: number; y: number }>;
}

interface NativeBarcodeDetectorClass {
  new (options?: { formats: string[] }): {
    detect(source: ImageBitmap | HTMLCanvasElement | HTMLVideoElement | ImageData): Promise<NativeBarcodeDetectorResult[]>;
  };
  getSupportedFormats(): Promise<string[]>;
}

const NativeBarcodeDetector = (typeof window !== 'undefined' &&
  'BarcodeDetector' in window)
  ? (window as unknown as { BarcodeDetector: NativeBarcodeDetectorClass }).BarcodeDetector
  : null;

/**
 * Race multiple decode promises — return the first non-null result.
 * Unlike Promise.all (which waits for all), or Promise.race (which returns
 * the first to settle regardless of value), this returns the first *successful*
 * (non-null) result, or null if all resolve to null.
 */
function raceForFirstResult(
  promises: Promise<string | null>[],
): Promise<string | null> {
  return new Promise((resolve) => {
    let resolved = false;
    let remaining = promises.length;

    if (remaining === 0) {
      resolve(null);
      return;
    }

    for (const p of promises) {
      p.then((result) => {
        if (resolved) return;
        if (result) {
          resolved = true;
          resolve(result);
        } else {
          remaining--;
          if (remaining === 0) resolve(null);
        }
      }).catch(() => {
        if (resolved) return;
        remaining--;
        if (remaining === 0) resolve(null);
      });
    }
  });
}

export function MLScanner({
  onScan,
  onError,
  onClose,
  className,
}: MLScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const decoderRef = useRef<Html5Qrcode | null>(null);
  const inferenceTimerRef = useRef<number | null>(null);
  const fullFrameTimerRef = useRef<number | null>(null);
  const hasScannedRef = useRef(false);
  const processFrameRef = useRef<(() => Promise<void>) | undefined>(undefined);
  const fullFrameDecodeRef = useRef<(() => Promise<void>) | undefined>(undefined);
  const onErrorRef = useRef(onError);
  const mlModelLoadedRef = useRef(false);

  const [scannerState, setScannerState] = useState<ScannerState>('initializing');
  const [statusMessage, setStatusMessage] = useState('Initializing camera...');
  const [errorMessage, setErrorMessage] = useState('');
  const [torchEnabled, setTorchEnabled] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [currentBbox, setCurrentBbox] = useState<BoundingBox | null>(null);
  const [stableFrameCount, setStableFrameCount] = useState(0);
  const [mlStatus, setMlStatus] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [retryCount, setRetryCount] = useState(0);

  const isBboxSimilar = useCallback(
    (a: BoundingBox, b: BoundingBox | null): boolean => {
      if (!b) return false;
      return (
        Math.abs(a.x - b.x) < BBOX_SIMILARITY_THRESHOLD &&
        Math.abs(a.y - b.y) < BBOX_SIMILARITY_THRESHOLD &&
        Math.abs(a.width - b.width) < BBOX_SIMILARITY_THRESHOLD &&
        Math.abs(a.height - b.height) < BBOX_SIMILARITY_THRESHOLD
      );
    },
    []
  );

  const cropImageData = useCallback(
    (
      imageData: ImageData,
      bbox: BoundingBox,
      padding: number = 0.15
    ): ImageData | null => {
      const { width, height, data } = imageData;

      const paddedX = Math.max(0, bbox.x - padding);
      const paddedY = Math.max(0, bbox.y - padding);
      const paddedWidth = Math.min(1 - paddedX, bbox.width + 2 * padding);
      const paddedHeight = Math.min(1 - paddedY, bbox.height + 2 * padding);

      const x = Math.floor(paddedX * width);
      const y = Math.floor(paddedY * height);
      const w = Math.floor(paddedWidth * width);
      const h = Math.floor(paddedHeight * height);

      if (w <= 0 || h <= 0) return null;

      const croppedData = new Uint8ClampedArray(w * h * 4);

      for (let row = 0; row < h; row++) {
        for (let col = 0; col < w; col++) {
          const srcIdx = ((y + row) * width + (x + col)) * 4;
          const dstIdx = (row * w + col) * 4;
          croppedData[dstIdx] = data[srcIdx];
          croppedData[dstIdx + 1] = data[srcIdx + 1];
          croppedData[dstIdx + 2] = data[srcIdx + 2];
          croppedData[dstIdx + 3] = data[srcIdx + 3];
        }
      }

      return new ImageData(croppedData, w, h);
    },
    []
  );

  const nativeBarcodeDetectorRef = useRef<InstanceType<NonNullable<typeof NativeBarcodeDetector>> | null>(null);

  // Decode using the native BarcodeDetector API
  // Filters formats via getSupportedFormats() to avoid silent failures
  // on browsers that don't support 'data_matrix'
  const decodeWithNativeDetector = useCallback(
    async (source: ImageData | HTMLVideoElement): Promise<string | null> => {
      if (!NativeBarcodeDetector) return null;

      try {
        if (!nativeBarcodeDetectorRef.current) {
          const supported = await NativeBarcodeDetector.getSupportedFormats();
          const wanted = [
            'data_matrix', 'qr_code', 'code_128', 'code_39',
            'ean_13', 'ean_8', 'upc_a', 'upc_e',
          ];
          const formats = wanted.filter((f) => supported.includes(f));
          if (formats.length === 0) return null;
          nativeBarcodeDetectorRef.current = new NativeBarcodeDetector({ formats });
        }

        const results = await nativeBarcodeDetectorRef.current.detect(source);
        if (results.length > 0 && results[0].rawValue) {
          return results[0].rawValue;
        }
      } catch {
        // Native detector not available or failed
      }
      return null;
    },
    []
  );

  const decodeWithHtml5Qrcode = useCallback(
    async (imageData: ImageData): Promise<string | null> => {
      const canvas = document.createElement('canvas');
      canvas.width = imageData.width;
      canvas.height = imageData.height;
      const ctx = canvas.getContext('2d')!;
      ctx.putImageData(imageData, 0, 0);

      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, 'image/png');
      });

      if (!blob) return null;

      const file = new File([blob], 'scan.png', { type: 'image/png' });

      try {
        if (!decoderRef.current) {
          decoderRef.current = new Html5Qrcode('ml-scanner-decoder', {
            formatsToSupport: SUPPORTED_FORMATS,
            verbose: false,
          });
        }

        const result = await decoderRef.current.scanFile(file, false);
        return result;
      } catch {
        return null;
      }
    },
    []
  );

  // Race all three decoders — return the first successful result.
  // Uses a true race pattern: the first non-null result wins immediately.
  const decodeImage = useCallback(
    async (imageData: ImageData): Promise<string | null> => {
      const result = await raceForFirstResult([
        decodeWithNativeDetector(imageData),
        decodeWithHtml5Qrcode(imageData),
        decodeWithZxingWasm(imageData),
      ]);
      return result;
    },
    [decodeWithNativeDetector, decodeWithHtml5Qrcode]
  );

  const captureAndDecode = useCallback(
    async (imageData: ImageData, bbox: BoundingBox) => {
      if (hasScannedRef.current) return;

      setScannerState('decoding');
      setStatusMessage('Decoding...');

      const paddingLevels = [0.15, 0.25, 0.1];

      for (const padding of paddingLevels) {
        if (hasScannedRef.current) return;
        const croppedImage = cropImageData(imageData, bbox, padding);
        if (croppedImage) {
          // Try all decoders on the crop, plus DM-specific decode
          // with preprocessing variants (contrast-enhanced, inverted)
          const result = await raceForFirstResult([
            decodeImage(croppedImage),
            decodeDataMatrixWithZxingWasm(croppedImage),
            ...getPreprocessedVariants(croppedImage).slice(1).map(
              (variant) => decodeDataMatrixWithZxingWasm(variant)
            ),
          ]);
          if (result && !hasScannedRef.current) {
            hasScannedRef.current = true;
            onScan(result);
            return;
          }
        }
      }

      if (hasScannedRef.current) return;
      const fullResult = await decodeImage(imageData);
      if (fullResult && !hasScannedRef.current) {
        hasScannedRef.current = true;
        onScan(fullResult);
        return;
      }

      setScannerState('ready');
      setStatusMessage('Could not decode. Try again.');
      setCurrentBbox(null);
      setStableFrameCount(0);
    },
    [cropImageData, decodeImage, onScan]
  );

  const processFrame = useCallback(async () => {
    if (
      !videoRef.current ||
      !canvasRef.current ||
      hasScannedRef.current ||
      scannerState === 'decoding' ||
      !mlModelLoadedRef.current
    ) {
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (video.readyState !== video.HAVE_ENOUGH_DATA) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(video, 0, 0);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

    const detection = await dataMatrixDetector.detect(imageData);

    if (detection.detected && detection.boundingBox) {
      const bbox = detection.boundingBox;

      if (isBboxSimilar(bbox, currentBbox)) {
        const newStableCount = stableFrameCount + 1;
        setStableFrameCount(newStableCount);

        if (newStableCount >= REQUIRED_STABLE_FRAMES) {
          await captureAndDecode(imageData, bbox);
          return;
        }
      } else {
        setStableFrameCount(1);
      }

      setCurrentBbox(bbox);
      setScannerState('detecting');
      setStatusMessage('Detected! Hold steady...');
    } else {
      if (stableFrameCount > 0) {
        setStableFrameCount(0);
        setCurrentBbox(null);
        setScannerState('ready');
        setStatusMessage('Point at barcode or QR code');
      }
    }
  }, [
    scannerState,
    currentBbox,
    stableFrameCount,
    isBboxSimilar,
    captureAndDecode,
  ]);

  // Keep refs up to date so the interval callbacks always use the latest version
  useEffect(() => { processFrameRef.current = processFrame; });

  // Periodic full-frame decode for 1D barcodes that the ML model can't detect
  const fullFrameDecode = useCallback(async () => {
    if (
      !videoRef.current ||
      !canvasRef.current ||
      hasScannedRef.current ||
      scannerState === 'decoding'
    ) {
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (video.readyState !== video.HAVE_ENOUGH_DATA) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(video, 0, 0);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

    // Race all decoders: native BarcodeDetector on video element,
    // Html5Qrcode and ZXing-C++ WASM on captured ImageData
    const result = await raceForFirstResult([
      decodeWithNativeDetector(video),
      decodeWithHtml5Qrcode(imageData),
      decodeWithZxingWasm(imageData),
    ]);
    if (result && !hasScannedRef.current) {
      hasScannedRef.current = true;
      onScan(result);
    }
  }, [scannerState, decodeWithNativeDetector, decodeWithHtml5Qrcode, onScan]);

  useEffect(() => { fullFrameDecodeRef.current = fullFrameDecode; });
  useEffect(() => { onErrorRef.current = onError; });

  useEffect(() => {
    let mounted = true;

    // Reset state for retry
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setScannerState('initializing');
    setStatusMessage('Initializing camera...');
    setErrorMessage('');
    setCurrentBbox(null);
    setStableFrameCount(0);
    hasScannedRef.current = false;

    const startCamera = async () => {
      try {
        console.log('[MLScanner] Requesting camera access...');
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'environment',
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        });

        if (!mounted) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;
        console.log('[MLScanner] Camera stream obtained');

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          try {
            await videoRef.current.play();
          } catch (playError) {
            console.warn('[MLScanner] video.play() failed, retrying after loadedmetadata...', playError);
            // On some mobile browsers play() can fail if called before metadata loads
            await new Promise<void>((resolve, reject) => {
              const video = videoRef.current;
              if (!video) { reject(new Error('video ref lost')); return; }
              if (video.readyState >= 1) {
                video.play().then(resolve).catch(reject);
              } else {
                const onLoaded = () => {
                  video.removeEventListener('loadedmetadata', onLoaded);
                  video.play().then(resolve).catch(reject);
                };
                video.addEventListener('loadedmetadata', onLoaded);
              }
            });
          }
          console.log(
            '[MLScanner] Video playing, dimensions:',
            videoRef.current.videoWidth, 'x', videoRef.current.videoHeight
          );
        } else {
          console.warn('[MLScanner] videoRef.current is null after getUserMedia');
        }

        const track = stream.getVideoTracks()[0];
        const capabilities = track.getCapabilities?.();
        if (capabilities && 'torch' in capabilities) {
          setTorchSupported(true);
        }

        if (!mounted) return;

        console.log('[MLScanner] Camera ready');
        setScannerState('ready');
        setStatusMessage('Point at barcode or QR code');

        // Start full-frame decode immediately so scanning works even
        // before the ML model finishes loading
        fullFrameTimerRef.current = window.setInterval(() => {
          fullFrameDecodeRef.current?.();
        }, FULL_FRAME_DECODE_INTERVAL_MS);
      } catch (error) {
        if (!mounted) return;

        console.error('[MLScanner] Camera init error:', error);
        const errorMsg =
          error instanceof Error ? error.message : 'Camera initialization failed';

        if (
          errorMsg.includes('Permission') ||
          errorMsg.includes('NotAllowed')
        ) {
          setScannerState('permission_denied');
          setErrorMessage(
            'Camera permission was denied. Please allow camera access.'
          );
        } else {
          setScannerState('error');
          setErrorMessage(errorMsg);
        }

        onErrorRef.current?.(errorMsg);
      }
    };

    // Load the ONNX model in the background — non-blocking.
    const loadModel = async () => {
      try {
        console.log('[MLScanner] Loading ML model...');
        await dataMatrixDetector.loadModel();
        console.log('[MLScanner] Model loaded successfully');

        if (!mounted) return;

        mlModelLoadedRef.current = true;
        setMlStatus('ready');

        // Start inference timer now that the model is ready
        inferenceTimerRef.current = window.setInterval(() => {
          processFrameRef.current?.();
        }, INFERENCE_INTERVAL_MS);
      } catch (error) {
        console.warn('[MLScanner] ML model failed to load, continuing with decode-only mode:', error);
        if (!mounted) return;
        setMlStatus('failed');
        // Scanner still works via full-frame decode — no need to propagate
        // this as a fatal error.
      }
    };

    startCamera();
    loadModel();

    return () => {
      mounted = false;

      if (inferenceTimerRef.current) {
        clearInterval(inferenceTimerRef.current);
        inferenceTimerRef.current = null;
      }

      if (fullFrameTimerRef.current) {
        clearInterval(fullFrameTimerRef.current);
        fullFrameTimerRef.current = null;
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }

      if (decoderRef.current) {
        decoderRef.current = null;
      }
    };
   
  }, [retryCount]);

  const toggleTorch = async () => {
    if (!streamRef.current || !torchSupported) return;

    try {
      const track = streamRef.current.getVideoTracks()[0];
      const newTorchState = !torchEnabled;
      await track.applyConstraints({
        advanced: [{ torch: newTorchState } as MediaTrackConstraintSet],
      });
      setTorchEnabled(newTorchState);
    } catch (error) {
      console.error('Failed to toggle torch:', error);
    }
  };

  const handleRetry = () => {
    // Clean up existing resources before reinitializing
    if (inferenceTimerRef.current) {
      clearInterval(inferenceTimerRef.current);
      inferenceTimerRef.current = null;
    }
    if (fullFrameTimerRef.current) {
      clearInterval(fullFrameTimerRef.current);
      fullFrameTimerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    decoderRef.current = null;
    // Trigger useEffect re-run to reinitialize camera
    setRetryCount((c) => c + 1);
  };

  return (
    <div
      className={cn(
        'relative bg-black rounded-lg overflow-hidden',
        className
      )}
      style={{ aspectRatio: '1 / 1', minHeight: 280 }}
    >
      {/* Video always rendered — visible as soon as stream attaches */}
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover"
        autoPlay
        playsInline
        muted
        style={{ transform: 'translateZ(0)' }}
      />

      <canvas ref={canvasRef} className="hidden" />

      <div id="ml-scanner-decoder" className="hidden" />

      {currentBbox && scannerState === 'detecting' && (
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute border-2 border-green-500 bg-green-500/20"
            style={{
              left: `${currentBbox.x * 100}%`,
              top: `${currentBbox.y * 100}%`,
              width: `${currentBbox.width * 100}%`,
              height: `${currentBbox.height * 100}%`,
            }}
          />
        </div>
      )}

      {scannerState === 'ready' && (
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative w-[250px] h-[250px]">
              <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-white rounded-tl" />
              <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-white rounded-tr" />
              <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-white rounded-bl" />
              <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-white rounded-br" />
            </div>
          </div>
        </div>
      )}

      {(scannerState === 'ready' ||
        scannerState === 'detecting' ||
        scannerState === 'decoding') && (
        <>
          <div className="absolute bottom-4 left-0 right-0 flex justify-center">
            <div className="bg-black/60 text-white text-sm px-4 py-2 rounded-full">
              {statusMessage}
            </div>
          </div>

          <div className="absolute top-4 right-4 flex gap-2">
            <Button
              variant="secondary"
              size="icon"
              onClick={toggleTorch}
              disabled={!torchSupported}
              className={cn(
                'text-white',
                torchSupported
                  ? 'bg-black/50 hover:bg-black/70'
                  : 'bg-black/30 opacity-50 cursor-not-allowed'
              )}
            >
              {torchEnabled ? (
                <Flashlight className="h-5 w-5" />
              ) : (
                <FlashlightOff className="h-5 w-5" />
              )}
            </Button>
          </div>

          <div className="absolute top-4 left-4">
            <div className={cn(
              'text-white text-xs px-2 py-1 rounded',
              mlStatus === 'ready' ? 'bg-green-500/80' :
              mlStatus === 'loading' ? 'bg-yellow-500/80' :
              'bg-gray-500/80'
            )}>
              {mlStatus === 'ready' ? 'Scanner Ready' :
               mlStatus === 'loading' ? 'Loading...' :
               'Scanner'}
            </div>
          </div>
        </>
      )}

      {scannerState === 'initializing' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 text-white">
          <Camera className="h-12 w-12 mb-4 animate-pulse" />
          <p className="text-sm">{statusMessage}</p>
        </div>
      )}

      {(scannerState === 'error' || scannerState === 'permission_denied') && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900 text-white p-6">
          <CameraOff className="h-12 w-12 mb-4 text-red-400" />
          <p className="text-center text-sm mb-4">{errorMessage}</p>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={handleRetry}>
              Try Again
            </Button>
            {onClose && (
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
